import { supabase } from '../../supabase';
import { ensureGymId, getCachedGymId } from './gymContext';
import { rowToMember } from './mappers';
import { invalidateCache } from '../cache/queryCache';
import { stampCreatedAt } from '../../utils/dbTimestamps';

/** Shown on members list / member record. */
export const WALK_IN_MEMBER_NAME = 'OXYGYM Walk-In Customer';

/** Shown on payments, invoices, and sale receipts. */
export const WALK_IN_PAYMENT_NAME = 'Walk-In Customer';

/** Constant public serial for every gym's walk-in member. */
export const WALK_IN_GYM_MEMBER_ID = '1';

const WALK_IN_PROFILE = {
  is_walk_in: true,
  firstName: 'OXYGYM',
  lastName: 'Walk-In Customer',
  status: 'active',
};

export function isWalkInGymMemberId(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  if (/^pending-/i.test(raw) || /^tmp-/i.test(raw) || /^gym-\d+$/i.test(raw)) return false;
  if (raw === WALK_IN_GYM_MEMBER_ID) return true;
  // UI serial format is ID-1 — do not treat GYM-1 / PENDING-…1 as walk-in
  const stripped = raw.replace(/^id[\s\-:_]*/i, '').trim();
  return stripped === WALK_IN_GYM_MEMBER_ID;
}

export function isWalkInMemberName(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return false;
  return (
    n === WALK_IN_PAYMENT_NAME.toLowerCase()
    || n === WALK_IN_MEMBER_NAME.toLowerCase()
    || n === 'walk-in customer'
    || n === 'walk in customer'
    || n.includes('walk-in')
    || n.includes('walk in')
  );
}

export function isWalkInMember(member) {
  if (!member) return false;
  if (member.isWalkIn || member.profile?.is_walk_in) return true;
  if (isWalkInGymMemberId(member.gymId || member.gym_member_id || member.baseGymId)) return true;
  if (isWalkInMemberName(member.name || member.full_name)) return true;
  return false;
}

function mapWalkInRow(row) {
  if (!row) return null;
  const member = rowToMember(row);
  return {
    ...member,
    gymId: WALK_IN_GYM_MEMBER_ID,
    name: WALK_IN_MEMBER_NAME,
    isWalkIn: true,
    serialNo: 1,
  };
}

/**
 * Ensure each gym has a permanent walk-in member (serial 1).
 * Safe to call repeatedly (idempotent).
 */
export async function ensureWalkInMemberForGym(gymId) {
  const gid = String(gymId || '').trim();
  if (!gid) throw new Error('Gym id is required to create walk-in member.');

  // Prefer profile flag (stable even if serial was changed)
  const { data: byFlagRows } = await supabase
    .from('members')
    .select('*')
    .eq('gym_id', gid)
    .eq('profile->>is_walk_in', 'true')
    .limit(1);
  const byFlag = Array.isArray(byFlagRows) ? byFlagRows[0] : byFlagRows;

  if (byFlag?.id) {
    const needsFix = (
      String(byFlag.gym_member_id || '').trim() !== WALK_IN_GYM_MEMBER_ID
      || String(byFlag.full_name || '').trim() !== WALK_IN_MEMBER_NAME
    );
    if (needsFix) {
      await supabase
        .from('members')
        .update({
          gym_member_id: WALK_IN_GYM_MEMBER_ID,
          full_name: WALK_IN_MEMBER_NAME,
          status: 'active',
          profile: { ...(byFlag.profile || {}), ...WALK_IN_PROFILE },
        })
        .eq('id', byFlag.id);
      const { data: refreshed } = await supabase
        .from('members')
        .select('*')
        .eq('id', byFlag.id)
        .maybeSingle();
      return mapWalkInRow(refreshed || byFlag);
    }
    return mapWalkInRow(byFlag);
  }

  const { data: bySerial } = await supabase
    .from('members')
    .select('*')
    .eq('gym_id', gid)
    .eq('gym_member_id', WALK_IN_GYM_MEMBER_ID)
    .limit(1)
    .maybeSingle();

  if (bySerial?.id) {
    // Serial 1 exists — mark it as the walk-in record for this gym
    await supabase
      .from('members')
      .update({
        full_name: WALK_IN_MEMBER_NAME,
        status: 'active',
        profile: { ...(bySerial.profile || {}), ...WALK_IN_PROFILE },
      })
      .eq('id', bySerial.id);
    const { data: refreshed } = await supabase
      .from('members')
      .select('*')
      .eq('id', bySerial.id)
      .maybeSingle();
    return mapWalkInRow(refreshed || bySerial);
  }

  const today = new Date().toISOString().slice(0, 10);
  const payload = {
    gym_id: gid,
    gym_member_id: WALK_IN_GYM_MEMBER_ID,
    full_name: WALK_IN_MEMBER_NAME,
    status: 'active',
    joining_date: today,
    profile: WALK_IN_PROFILE,
  };

  const { data: inserted, error } = await supabase
    .from('members')
    .insert(stampCreatedAt(payload))
    .select('*')
    .single();

  if (error) {
    // Race: another session created it
    if (/duplicate|unique/i.test(error.message || '')) {
      const { data: again } = await supabase
        .from('members')
        .select('*')
        .eq('gym_id', gid)
        .eq('gym_member_id', WALK_IN_GYM_MEMBER_ID)
        .limit(1)
        .maybeSingle();
      if (again) return mapWalkInRow(again);
    }
    throw error;
  }

  try {
    invalidateCache(`gym:${gid}:`);
  } catch {
    /* ignore */
  }

  return mapWalkInRow(inserted);
}

/** Ensure walk-in for the currently active gym. */
export async function ensureWalkInMember() {
  const gid = getCachedGymId() || await ensureGymId();
  return ensureWalkInMemberForGym(gid);
}

/** Backfill walk-in members for every gym (admin/owner maintenance). */
export async function ensureWalkInMembersForAllGyms() {
  const { data: gyms, error } = await supabase.from('gyms').select('id');
  if (error) throw error;
  const results = [];
  for (const gym of gyms || []) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await ensureWalkInMemberForGym(gym.id));
  }
  return results;
}
